/**
 * SinglePly — traditional systems (TPO / EPDM / PVC / ModBit) estimator +
 * distributor price-list manager. Membrane/insulation/adhesive quantities
 * auto-calc from roof SF + waste %; unit items (drains, boots) are manual.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api, API, formatCurrency } from "@/lib/api";
import { toast } from "sonner";
import { Layers, Upload, Trash2, Plus, ChevronUp, ChevronDown, Save, FileText, Package } from "lucide-react";
import { ScopeLaneSwitcher } from "@/components/ScopeLaneSwitcher";

const ATTACHMENTS = ["Fully Adhered", "Mechanically Fastened", "Induction Weld"];
const PREP = ["Recover (over existing)", "Tear-Off"];
const DEFAULT_ORDER = ["basics", "supply", "membrane", "insulation", "adhesives", "units", "pricing"];

const ceilQty = (sf, waste, coverage) => (coverage > 0 ? Math.ceil((sf * (1 + waste / 100)) / coverage) : 0);

function Section({ id, title, order, setOrder, children }) {
  const idx = order.indexOf(id);
  const move = (dir) => {
    const next = [...order];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setOrder(next);
    localStorage.setItem("sp_section_order", JSON.stringify(next));
  };
  return (
    <div className="bg-white border border-zinc-200 rounded-sm mb-4" data-testid={`sp-section-${id}`}>
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-zinc-100">
        <span className="text-[10px] font-black text-zinc-300">{idx + 1}</span>
        <span className="text-[11px] font-bold uppercase tracking-wider">{title}</span>
        <span className="ml-auto flex gap-0.5">
          <button onClick={() => move(-1)} className="p-1 hover:bg-zinc-100 rounded-sm disabled:opacity-30" disabled={idx === 0} data-testid={`sp-move-up-${id}`}><ChevronUp className="w-3.5 h-3.5" /></button>
          <button onClick={() => move(1)} className="p-1 hover:bg-zinc-100 rounded-sm disabled:opacity-30" disabled={idx === order.length - 1} data-testid={`sp-move-down-${id}`}><ChevronDown className="w-3.5 h-3.5" /></button>
        </span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

const inp = "h-9 px-2 border border-zinc-300 rounded-sm text-sm bg-white w-full";
const lbl = "block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1";

function ProductRows({ rows, setRows, products, autoQty, sf, waste, qtyLabel }) {
  const upd = (i, patch) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  return (
    <div className="space-y-2">
      {rows.map((r, i) => {
        const p = products.find((x) => x.id === r.product_id);
        const cov = r.coverage ?? (p?.coverage_sqft || 0);
        const qty = autoQty ? ceilQty(sf, waste, cov) : Number(r.qty || 0);
        const cost = qty * (p?.price || 0);
        return (
          <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`sp-row-${i}`}>
            <select className={`${inp} flex-1 min-w-[220px]`} value={r.product_id} onChange={(e) => upd(i, { product_id: e.target.value, coverage: undefined })}>
              <option value="">— pick product —</option>
              {products.map((x) => {
                const optLabel = `${x.section !== x.name ? `${x.section} · ` : ""}${x.name} — ${formatCurrency(x.price)}/${x.uom || "ea"}`;
                return <option key={x.id} value={x.id}>{optLabel}</option>;
              })}
            </select>
            {autoQty ? (
              <input type="number" className={`${inp} w-28`} title="Coverage SF per unit" value={cov || ""} onChange={(e) => upd(i, { coverage: Number(e.target.value) })} placeholder="SF/unit" />
            ) : (
              <input type="number" className={`${inp} w-20`} value={r.qty || ""} onChange={(e) => upd(i, { qty: e.target.value })} placeholder={qtyLabel || "Qty"} />
            )}
            <span className="font-mono text-xs w-20 text-right">{qty > 0 ? `${qty}×` : "—"}</span>
            <span className="font-mono text-xs font-bold w-24 text-right">{cost > 0 ? formatCurrency(cost) : "—"}</span>
            <button onClick={() => setRows(rows.filter((_, j) => j !== i))} className="p-1.5 hover:bg-red-50 rounded-sm"><Trash2 className="w-3.5 h-3.5 text-red-600" /></button>
          </div>
        );
      })}
      <button onClick={() => setRows([...rows, { product_id: "", qty: "" }])} className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider border border-zinc-300 px-2 h-8 rounded-sm hover:border-blue-700 hover:text-blue-700" data-testid="sp-add-row">
        <Plus className="w-3 h-3" /> Add item
      </button>
    </div>
  );
}

export default function SinglePly() {
  const [tab, setTab] = useState("estimator");
  const [opts, setOpts] = useState({ distributors: [], product_lines: [], loaded_lines: [], categories: [] });
  const [products, setProducts] = useState([]);
  const [deals, setDeals] = useState([]);
  const [order, setOrder] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("sp_section_order") || "null");
      return Array.isArray(saved) && saved.length === DEFAULT_ORDER.length ? saved : DEFAULT_ORDER;
    } catch { return DEFAULT_ORDER; }
  });

  // estimator state
  const [sf, setSf] = useState("");
  const [waste, setWaste] = useState(10);
  const [prep, setPrep] = useState(PREP[0]);
  const [attach, setAttach] = useState(ATTACHMENTS[0]);
  const [distributor, setDistributor] = useState("");
  const [line, setLine] = useState("");
  const [membraneRows, setMembraneRows] = useState([{ product_id: "" }]);
  const [insulRows, setInsulRows] = useState([]);
  const [adhRows, setAdhRows] = useState([]);
  const [unitRows, setUnitRows] = useState([]);
  const [laborRate, setLaborRate] = useState(3.5);
  const [margin, setMargin] = useState(30);
  const [w15, setW15] = useState(0);
  const [w20, setW20] = useState(0);
  const [dealId, setDealId] = useState("");
  const [estName, setEstName] = useState("");
  const [estimates, setEstimates] = useState([]);
  const [estId, setEstId] = useState("");

  const loadOpts = () => api.get("/sp/options").then((r) => setOpts(r.data)).catch(() => {});
  useEffect(() => {
    loadOpts();
    api.get("/deals").then((r) => setDeals(r.data || [])).catch(() => {});
    api.get("/sp/estimates").then((r) => setEstimates(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (!distributor || !line) { setProducts([]); return; }
    api.get(`/sp/products?distributor=${encodeURIComponent(distributor)}&product_line=${encodeURIComponent(line)}`)
      .then((r) => setProducts(r.data || [])).catch(() => setProducts([]));
  }, [distributor, line]);

  const byCat = (c) => products.filter((p) => p.category === c);
  const sfN = Number(sf || 0);

  const rowsCost = (rows, cat, autoQty) => rows.reduce((s, r) => {
    const p = products.find((x) => x.id === r.product_id);
    if (!p) return s;
    const cov = r.coverage ?? (p.coverage_sqft || 0);
    const qty = autoQty ? ceilQty(sfN, waste, cov) : Number(r.qty || 0);
    return s + qty * p.price;
  }, 0);
  const totals = useMemo(() => {
    const membrane = rowsCost(membraneRows, "Membrane", true);
    const insul = rowsCost(insulRows, "", true);
    const adh = rowsCost(adhRows, "", true);
    const units = rowsCost(unitRows, "", false);
    const materials = membrane + insul + adh + units;
    const labor = sfN * Number(laborRate || 0);
    const jobCost = materials + labor;
    const m = Number(margin || 0);
    const baseSell = m < 100 ? jobCost / (1 - m / 100) : jobCost;
    return {
      membrane, insul, adh, units, total: materials, labor, jobCost,
      perSf: sfN > 0 ? materials / sfN : 0,
      sell15: Math.round(baseSell + Number(w15 || 0)),
      sell20: Math.round(baseSell + Number(w20 || 0)),
    };
  }, [membraneRows, insulRows, adhRows, unitRows, products, sfN, waste, laborRate, margin, w15, w20]); // eslint-disable-line

  const saveEstimate = async () => {
    if (!estName.trim()) { toast.error("Give the estimate a name"); return; }
    try {
      const { data } = await api.post("/sp/estimates", {
        id: estId || undefined, name: estName, deal_id: dealId,
        data: { sf: sfN, waste, prep, attach, distributor, line, membraneRows, insulRows, adhRows, unitRows, laborRate, margin, w15, w20 },
        total_cost: totals.jobCost, cost_per_sqft: sfN > 0 ? totals.jobCost / sfN : 0,
      });
      setEstId(data.id);
      toast.success("Estimate saved");
      api.get("/sp/estimates").then((r) => setEstimates(r.data || []));
    } catch { toast.error("Save failed"); }
  };
  const loadEstimate = (e) => {
    const d = e.data || {};
    setEstId(e.id); setEstName(e.name); setDealId(e.deal_id || "");
    setSf(d.sf || ""); setWaste(d.waste ?? 10); setPrep(d.prep || PREP[0]); setAttach(d.attach || ATTACHMENTS[0]);
    setDistributor(d.distributor || ""); setLine(d.line || "");
    setMembraneRows(d.membraneRows || [{ product_id: "" }]);
    setInsulRows(d.insulRows || []); setAdhRows(d.adhRows || []); setUnitRows(d.unitRows || []);
    setLaborRate(d.laborRate ?? 3.5); setMargin(d.margin ?? 30); setW15(d.w15 || 0); setW20(d.w20 || 0);
    toast.success(`Loaded "${e.name}"`);
  };

  const sections = {
    basics: (
      <Section key="basics" id="basics" title="Job Basics" order={order} setOrder={setOrder}>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div><label className={lbl}>Roof Size (SF)</label><input type="number" className={inp} value={sf} onChange={(e) => setSf(e.target.value)} data-testid="sp-sf" /></div>
          <div><label className={lbl}>Waste %</label><input type="number" className={inp} value={waste} onChange={(e) => setWaste(Number(e.target.value))} data-testid="sp-waste" /></div>
          <div><label className={lbl}>Prep</label><select className={inp} value={prep} onChange={(e) => setPrep(e.target.value)} data-testid="sp-prep">{PREP.map((x) => <option key={x}>{x}</option>)}</select></div>
          <div><label className={lbl}>Attachment</label><select className={inp} value={attach} onChange={(e) => setAttach(e.target.value)} data-testid="sp-attach">{ATTACHMENTS.map((x) => <option key={x}>{x}</option>)}</select></div>
          <div><label className={lbl}>Link to Deal (optional)</label>
            <select className={inp} value={dealId} onChange={(e) => {
              setDealId(e.target.value);
              const d = deals.find((x) => x.id === e.target.value);
              if (d?.total_sqft && !sf) setSf(String(d.total_sqft));
            }} data-testid="sp-deal">
              <option value="">— none —</option>
              {deals.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
            </select>
          </div>
        </div>
        {attach === "Mechanically Fastened" && <div className="text-[11px] text-amber-700 mt-2">Mechanically fastened: add plates/fasteners under Accessories & Unit Items below.</div>}
      </Section>
    ),
    supply: (
      <Section key="supply" id="supply" title="Distributor & Product Line" order={order} setOrder={setOrder}>
        <div className="grid grid-cols-2 gap-3 max-w-lg">
          <div><label className={lbl}>Distributor</label>
            <select className={inp} value={distributor} onChange={(e) => setDistributor(e.target.value)} data-testid="sp-distributor">
              <option value="">— pick —</option>
              {opts.distributors.map((d) => <option key={d}>{d}</option>)}
            </select>
          </div>
          <div><label className={lbl}>Product Line</label>
            <select className={inp} value={line} onChange={(e) => setLine(e.target.value)} data-testid="sp-line">
              <option value="">— pick —</option>
              {opts.loaded_lines.map((l) => <option key={l}>{l}</option>)}
            </select>
          </div>
        </div>
        {opts.distributors.length === 0 && <div className="text-xs text-zinc-500 mt-2">No price lists loaded yet — upload one under the Price Lists tab.</div>}
      </Section>
    ),
    membrane: (
      <Section key="membrane" id="membrane" title="Membrane (auto from SF + waste)" order={order} setOrder={setOrder}>
        <ProductRows rows={membraneRows} setRows={setMembraneRows} products={byCat("Membrane")} autoQty sf={sfN} waste={waste} />
      </Section>
    ),
    insulation: (
      <Section key="insulation" id="insulation" title="Insulation, Coverboard & Vapor Retarder (auto)" order={order} setOrder={setOrder}>
        <ProductRows rows={insulRows} setRows={setInsulRows} products={[...byCat("Insulation & Separation"), ...byCat("Vapor Retarder")]} autoQty sf={sfN} waste={waste} />
      </Section>
    ),
    adhesives: (
      <Section key="adhesives" id="adhesives" title="Adhesives & Sealants (auto — set SF coverage per unit)" order={order} setOrder={setOrder}>
        <ProductRows rows={adhRows} setRows={setAdhRows} products={byCat("Adhesives & Sealants")} autoQty sf={sfN} waste={waste} />
      </Section>
    ),
    units: (
      <Section key="units" id="units" title="Accessories, Drains & Unit Items (manual counts)" order={order} setOrder={setOrder}>
        <ProductRows rows={unitRows} setRows={setUnitRows} products={[...byCat("Accessories"), ...byCat("Drains")]} autoQty={false} qtyLabel="Count" />
      </Section>
    ),
    pricing: (
      <Section key="pricing" id="pricing" title="Labor & Sell Price" order={order} setOrder={setOrder}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><label className={lbl}>Labor ($/SF)</label><input type="number" step="0.05" className={inp} value={laborRate} onChange={(e) => setLaborRate(e.target.value)} data-testid="sp-labor-rate" />
            <div className="text-[9px] text-zinc-400 mt-0.5">= ${(Number(laborRate || 0) * 100).toFixed(0)}/square</div></div>
          <div><label className={lbl}>Margin %</label><input type="number" className={inp} value={margin} onChange={(e) => setMargin(e.target.value)} data-testid="sp-margin" /></div>
          <div><label className={lbl}>15-Yr Warranty Add ($)</label><input type="number" className={inp} value={w15} onChange={(e) => setW15(e.target.value)} data-testid="sp-w15" /></div>
          <div><label className={lbl}>20-Yr Warranty Add ($)</label><input type="number" className={inp} value={w20} onChange={(e) => setW20(e.target.value)} data-testid="sp-w20" /></div>
        </div>
        <div className="text-[11px] text-zinc-500 mt-2">Warranty adders default to $0 until manufacturer warranty costs are set. Sell price = (materials + labor) ÷ (1 − margin) + warranty add.</div>
      </Section>
    ),
  };

  return (
    <div className="p-6 lg:p-8" data-testid="single-ply-page">
      <div className="mb-5"><ScopeLaneSwitcher active="traditional" /></div>
      <div className="flex items-center gap-3 mb-4">
        <Layers className="w-6 h-6 text-blue-700" />
        <h1 className="font-heading text-2xl font-black tracking-tight">Single-Ply / Traditional Systems</h1>
      </div>
      <div className="flex gap-1 border-b border-zinc-200 mb-6">
        {[["estimator", "Estimator"], ["lists", "Price Lists"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`sp-tab-${k}`}
            className={`px-4 h-10 text-xs font-bold uppercase tracking-wider border-b-2 -mb-px ${tab === k ? "border-blue-700 text-blue-700" : "border-transparent text-zinc-500 hover:text-zinc-800"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "estimator" && (
        <div className="max-w-4xl">
          {order.map((id) => sections[id])}
          <div className="bg-zinc-950 text-white rounded-sm p-5 mb-4" data-testid="sp-summary">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-center mb-4">
              {[["Membrane", totals.membrane], ["Insulation", totals.insul], ["Adhesives", totals.adh], ["Unit Items", totals.units]].map(([k, v]) => (
                <div key={k}><div className="text-[9px] font-bold uppercase tracking-wider text-zinc-400">{k}</div><div className="font-mono font-bold">{formatCurrency(v)}</div></div>
              ))}
              <div><div className="text-[9px] font-bold uppercase tracking-wider text-zinc-400">Materials</div>
                <div className="font-mono font-bold" data-testid="sp-total">{formatCurrency(totals.total)}</div>
                <div className="text-[10px] text-zinc-400 font-mono">{sfN > 0 ? `$${totals.perSf.toFixed(2)}/SF` : ""}</div>
              </div>
              <div><div className="text-[9px] font-bold uppercase tracking-wider text-zinc-400">Labor</div>
                <div className="font-mono font-bold" data-testid="sp-labor">{formatCurrency(totals.labor)}</div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 text-center border-t border-zinc-700 pt-4">
              <div><div className="text-[9px] font-bold uppercase tracking-wider text-amber-400">Total Job Cost</div>
                <div className="font-mono font-black text-lg" data-testid="sp-job-cost">{formatCurrency(totals.jobCost)}</div>
                <div className="text-[10px] text-zinc-400 font-mono">{sfN > 0 ? `$${(totals.jobCost / sfN).toFixed(2)}/SF` : ""}</div>
              </div>
              <div><div className="text-[9px] font-bold uppercase tracking-wider text-emerald-400">Sell — 15-Yr Warranty</div>
                <div className="font-mono font-black text-lg" data-testid="sp-sell-15">{formatCurrency(totals.sell15)}</div>
              </div>
              <div><div className="text-[9px] font-bold uppercase tracking-wider text-emerald-400">Sell — 20-Yr Warranty</div>
                <div className="font-mono font-black text-lg" data-testid="sp-sell-20">{formatCurrency(totals.sell20)}</div>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-8">
            <input className={`${inp} max-w-xs`} placeholder="Estimate name (e.g. 1860 Washington — PVC 60)" value={estName} onChange={(e) => setEstName(e.target.value)} data-testid="sp-est-name" />
            <button onClick={saveEstimate} className="inline-flex items-center gap-1.5 h-9 px-4 bg-blue-700 text-white text-xs font-bold uppercase tracking-wider rounded-sm hover:bg-blue-800" data-testid="sp-save-estimate">
              <Save className="w-3.5 h-3.5" /> {estId ? "Update Estimate" : "Save Estimate"}
            </button>
            {estId && <button onClick={() => { setEstId(""); setEstName(""); }} className="h-9 px-3 border border-zinc-300 text-[10px] font-bold uppercase tracking-wider rounded-sm">Save as New</button>}
            {estId && (
              <>
                <button onClick={() => window.open(`${API}/sp/estimates/${estId}/scope-pdf?token=${encodeURIComponent(localStorage.getItem("crm_token") || "")}`, "_blank")}
                  className="inline-flex items-center gap-1.5 h-9 px-3 border border-blue-700 text-blue-700 text-[10px] font-bold uppercase tracking-wider rounded-sm hover:bg-blue-50" data-testid="sp-scope-pdf">
                  <FileText className="w-3.5 h-3.5" /> Scope PDF
                </button>
                <button onClick={() => window.open(`${API}/sp/estimates/${estId}/po-pdf?token=${encodeURIComponent(localStorage.getItem("crm_token") || "")}`, "_blank")}
                  className="inline-flex items-center gap-1.5 h-9 px-3 border border-zinc-950 text-[10px] font-bold uppercase tracking-wider rounded-sm hover:bg-zinc-100" data-testid="sp-po-pdf">
                  <Package className="w-3.5 h-3.5" /> Material Order PDF
                </button>
              </>
            )}
          </div>
          {estimates.length > 0 && (
            <div className="border border-zinc-200 rounded-sm bg-white" data-testid="sp-estimates-list">
              <div className="px-4 py-2 text-[10px] font-bold uppercase tracking-wider text-zinc-500 border-b border-zinc-100">Saved Estimates</div>
              {estimates.map((e) => (
                <div key={e.id} className="flex items-center gap-3 px-4 py-2 border-b border-zinc-50 hover:bg-zinc-50">
                  <button onClick={() => loadEstimate(e)} className="font-bold text-sm hover:text-blue-700 text-left" data-testid={`sp-load-${e.id}`}>{e.name}</button>
                  {e.deal_id && <span className="text-[10px] uppercase text-zinc-400">{deals.find((d) => d.id === e.deal_id)?.title || "deal"}</span>}
                  <span className="ml-auto font-mono text-xs font-bold">{formatCurrency(e.total_cost)}</span>
                  <span className="font-mono text-[10px] text-zinc-400">${Number(e.cost_per_sqft || 0).toFixed(2)}/SF</span>
                  <button onClick={async () => { await api.delete(`/sp/estimates/${e.id}`); setEstimates(estimates.filter((x) => x.id !== e.id)); }} className="p-1 hover:bg-red-50 rounded-sm"><Trash2 className="w-3 h-3 text-red-600" /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "lists" && <PriceLists onChanged={loadOpts} />}
    </div>
  );
}

export function PriceLists({ onChanged }) {
  const [opts, setOpts] = useState({ distributors: [], product_lines: [], loaded_lines: [], categories: [] });
  const loadOpts = () => api.get("/sp/options").then((r) => setOpts(r.data)).catch(() => {});
  useEffect(() => { loadOpts(); }, []);
  const [distributor, setDistributor] = useState("");
  const [manufacturer, setManufacturer] = useState("");
  const [line, setLine] = useState("PVC");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [browse, setBrowse] = useState({ distributor: "", line: "" });
  const [rows, setRows] = useState([]);

  useEffect(() => {
    // Auto-select the first loaded price list so products show immediately
    if (!browse.distributor && opts.distributors.length > 0 && opts.loaded_lines.length > 0) {
      setBrowse({ distributor: opts.distributors[0], line: opts.loaded_lines[0] });
    }
  }, [opts]); // eslint-disable-line

  useEffect(() => {
    if (!browse.distributor || !browse.line) { setRows([]); return; }
    api.get(`/sp/products?distributor=${encodeURIComponent(browse.distributor)}&product_line=${encodeURIComponent(browse.line)}`)
      .then((r) => setRows(r.data || [])).catch(() => setRows([]));
  }, [browse]);

  const upload = async () => {
    if (!file || !distributor.trim() || !line) { toast.error("Pick a file, distributor, and product line"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("distributor", distributor.trim());
      fd.append("manufacturer", manufacturer.trim());
      fd.append("product_line", line);
      const { data } = await api.post("/sp/import-xlsx", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`Imported ${data.inserted} new, ${data.updated} updated`);
      loadOpts();
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    setBusy(false);
  };

  return (
    <div className="max-w-4xl">
      <div className="bg-white border border-zinc-200 rounded-sm p-5 mb-6" data-testid="sp-import-card">
        <div className="text-[11px] font-bold uppercase tracking-wider mb-3">Upload a Price List (Excel or CSV)</div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
          <div><label className={lbl}>Distributor</label><input className={inp} list="sp-dists" value={distributor} onChange={(e) => setDistributor(e.target.value)} placeholder="ABC Supply, GAF Direct…" data-testid="sp-import-distributor" />
            <datalist id="sp-dists">{opts.distributors.map((d) => <option key={d} value={d} />)}</datalist></div>
          <div><label className={lbl}>Manufacturer</label><input className={inp} value={manufacturer} onChange={(e) => setManufacturer(e.target.value)} placeholder="GAF" data-testid="sp-import-manufacturer" /></div>
          <div><label className={lbl}>Product Line</label><select className={inp} value={line} onChange={(e) => setLine(e.target.value)} data-testid="sp-import-line">{opts.product_lines.map((l) => <option key={l}>{l}</option>)}</select></div>
          <div><label className={lbl}>Price List File</label><input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-xs" data-testid="sp-import-file" /></div>
        </div>
        <button onClick={upload} disabled={busy} className="inline-flex items-center gap-1.5 h-9 px-4 bg-blue-700 text-white text-xs font-bold uppercase tracking-wider rounded-sm hover:bg-blue-800 disabled:opacity-50" data-testid="sp-import-submit">
          <Upload className="w-3.5 h-3.5" /> {busy ? "Importing…" : "Import"}
        </button>
        {result && (
          <div className="mt-3 text-xs" data-testid="sp-import-result">
            <b>Done.</b> {result.inserted} new, {result.updated} updated — {Object.entries(result.by_category || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}
          </div>
        )}
        <div className="text-[10px] text-zinc-400 mt-2">Re-uploading the same list updates prices (matched by item code) — no duplicates. Works with GAF-format lists like the PVC T787 sheet.</div>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <select className={`${inp} max-w-[200px]`} value={browse.distributor} onChange={(e) => setBrowse({ ...browse, distributor: e.target.value })} data-testid="sp-browse-distributor">
          <option value="">— distributor —</option>
          {opts.distributors.map((d) => <option key={d}>{d}</option>)}
        </select>
        <select className={`${inp} max-w-[160px]`} value={browse.line} onChange={(e) => setBrowse({ ...browse, line: e.target.value })} data-testid="sp-browse-line">
          <option value="">— line —</option>
          {opts.loaded_lines.map((l) => <option key={l}>{l}</option>)}
        </select>
        {rows.length > 0 && <span className="text-xs text-zinc-500">{rows.length} products</span>}
      </div>
      {rows.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-sm overflow-x-auto" data-testid="sp-browse-table">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[9px] uppercase tracking-wider text-zinc-400 border-b border-zinc-100">
              <th className="px-3 py-2">Product</th><th className="px-2 py-2">Item Code</th><th className="px-2 py-2 text-right">Price</th><th className="px-2 py-2">UOM</th><th className="px-2 py-2 text-right">SF/Unit</th><th className="px-2 py-2 text-right">$/SF</th>
            </tr></thead>
            <tbody>
              {(() => {
                const rank = { "Membrane": 0, "Accessories": 1, "Drains": 2, "Adhesives & Sealants": 3, "Vapor Retarder": 4, "Insulation & Separation": 5 };
                const sections = [];
                for (const r of rows) {
                  const key = r.section || r.category;
                  const g = sections.find((s) => s.key === key);
                  if (g) g.items.push(r); else sections.push({ key, cat: r.category, items: [r] });
                }
                sections.sort((a, b) => (rank[a.cat] ?? 9) - (rank[b.cat] ?? 9));
                return sections.map((g) => (
                  <React.Fragment key={g.key}>
                    <tr className="bg-[#0F4C81]" data-testid={`sp-section-header-${g.key}`}>
                      <td colSpan={6} className="px-3 py-1.5 text-white font-bold text-[10px] uppercase tracking-wider">{g.key} <span className="font-normal text-[#A9C7E3]">({g.items.length})</span></td>
                    </tr>
                    {g.items.map((r) => (
                      <tr key={r.id} className="border-b border-zinc-50 hover:bg-zinc-50">
                        <td className="px-3 py-1.5 font-medium">{r.name}</td>
                        <td className="px-2 py-1.5 font-mono text-zinc-500">{r.item_code}</td>
                        <td className="px-2 py-1.5 text-right font-mono font-bold">{`$${Number(r.price).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}</td>
                        <td className="px-2 py-1.5">{r.uom}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{r.coverage_sqft ? Math.round(r.coverage_sqft * 100) / 100 : "—"}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{r.price_per_sqft ? `$${r.price_per_sqft.toFixed(3)}` : "—"}</td>
                      </tr>
                    ))}
                  </React.Fragment>
                ));
              })()}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
